import { Contact, ContactType } from '@api/generated/graphql';
import CallingDisabled from '@components/CallingDisabledGuard/CallingDisabledGuard';
import useCustomerContext from '@components/CustomerContext/useCustomerContext';
import useContactMethodsMenuContext from '@components/CustomerSearch/components/CustomerSearchResultItem/components/ContactMethodsMenu/context/useContactMethodsMenuContext';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import { Popover } from '@servicetitan/design-system';
import { FC, ReactElement, useState } from 'react';

const ClickToCall: FC<{
  element: (onClick: () => void) => ReactElement;
  contact: Contact;
  customTaskAttributes?: Record<string, unknown>;
}> = ({ element, contact, customTaskAttributes }) => {
  const { user } = useAuth();
  const { phoneConnected } = usePhoneState();
  const { setShouldCloseOnOutsideClick } = useContactMethodsMenuContext();
  const { customerName, tenantId, customerId } = useCustomerContext();
  const [showPopover, setShowPopover] = useState(false);
  const handleContactValueClick = () => {
    if (isPhoneOrMobileType && !SoftPhoneModule.isInCall() && phoneConnected) {
      if (user?.outboundCallsAllowed) {
        setShowPopover(false);
        ModalsModule.openModal(ModalId.CallConfirmation, {
          contact,
          onClose: handleCloseCallConfirm,
          customTaskAttributes: customTaskAttributes,
          customer: {
            customerName,
            tenantId,
            customerId,
          },
        });
        setShouldCloseOnOutsideClick?.(false);
      } else {
        setShowPopover(true);
      }
    }
  };

  const isPhoneOrMobileType =
    contact.type === ContactType.MobilePhone ||
    contact.type === ContactType.Phone;

  const handleCloseCallConfirm = () => {
    ModalsModule.closeActiveModal();
    setShouldCloseOnOutsideClick?.(true);
  };

  return (
    <Popover
      text='Tooltip'
      open={showPopover}
      onClickOutside={() => setShowPopover(false)}
      className='bg-neutral-30'
      direction='b'
      portal={true}
      trigger={
        isPhoneOrMobileType ? (
          <CallingDisabled>{element(handleContactValueClick)}</CallingDisabled>
        ) : (
          element(handleContactValueClick)
        )
      }
    >
      <div className='z-notification-i'>
        Click to call is unavailable. Please complete the following steps to
        enable outbound calls:
        <ul>
          <li>A basic or advanced user seat</li>
          <li>Assigned to a phone number</li>
          <li>E911 address configured</li>
        </ul>
      </div>
    </Popover>
  );
};

export default ClickToCall;
